import { useMemo, useState } from 'react'
import Breadcrumbs from '@/components/Breadcrumbs'
import DisclaimerNote from '@/components/DisclaimerNote'
import FaqAccordion from '@/components/FaqAccordion'
import NumberInput from '@/components/ui/NumberInput'
import SectionHeading from '@/components/ui/SectionHeading'
import StatCard from '@/components/ui/StatCard'
import Seo from '@/components/Seo'
import { formatCurrency } from '@/lib/format'
import { breadcrumbSchema, faqPageSchema, softwareApplicationSchema } from '@/lib/schema'

type Network = 'ethereum' | 'polygon'

const networkDefaults: Record<Network, { gasPrice: number, gasLimit: number, tokenPrice: number, symbol: string }> = {
  ethereum: { gasPrice: 15, gasLimit: 120000, tokenPrice: 3400, symbol: 'ETH' },
  polygon: { gasPrice: 40, gasLimit: 120000, tokenPrice: 0.55, symbol: 'MATIC' },
}

const faqItems = [
  { question: 'How is NFT mint gas cost calculated?', answer: 'Gas cost = Gas Price (in Gwei) × Gas Limit, converted from Gwei to the native token, then multiplied by the token USD price. Total mint cost adds the mint price plus gas fees.' },
  { question: 'Why does gas price fluctuate so much on Ethereum?', answer: 'Gas prices reflect real-time network demand. High-traffic mint events or NFT drops spike demand for block space, driving Gwei prices up temporarily.' },
  { question: 'What is a breakeven resale floor price?', answer: 'It is the minimum secondary market price needed to recover your total mint cost (mint price + gas + marketplace fees), accounting for marketplace and royalty percentages taken on resale.' },
]

function NftMintCost() {
  const [network, setNetwork] = useState<Network>('ethereum')
  const [mintPrice, setMintPrice] = useState(0.08)
  const [quantity, setQuantity] = useState(1)
  const [gasPrice, setGasPrice] = useState(networkDefaults.ethereum.gasPrice)
  const [gasLimit, setGasLimit] = useState(networkDefaults.ethereum.gasLimit)
  const [tokenPrice, setTokenPrice] = useState(networkDefaults.ethereum.tokenPrice)
  const [royaltyPct, setRoyaltyPct] = useState(5)

  const handleNetworkChange = (net: Network) => {
    setNetwork(net)
    setGasPrice(networkDefaults[net].gasPrice)
    setGasLimit(networkDefaults[net].gasLimit)
    setTokenPrice(networkDefaults[net].tokenPrice)
  }

  const results = useMemo(() => {
    const gasCostNative = (gasPrice * gasLimit) / 1e9
    const totalGasNative = gasCostNative * quantity
    const totalMintNative = mintPrice * quantity
    const totalNative = totalMintNative + totalGasNative
    const totalUsd = totalNative * tokenPrice
    const gasUsd = totalGasNative * tokenPrice
    const costPerItem = totalUsd / quantity
    const breakevenFloor = costPerItem / (1 - royaltyPct / 100)
    const safetyBuffer = totalUsd * 1.15
    return { gasCostNative, totalGasNative, totalNative, totalUsd, gasUsd, costPerItem, breakevenFloor, safetyBuffer }
  }, [mintPrice, quantity, gasPrice, gasLimit, tokenPrice, royaltyPct])

  const symbol = networkDefaults[network].symbol

  return (
    <>
      <Seo
        title="NFT Mint Cost & Gas Estimator - Ethereum & Polygon"
        description="Free NFT mint cost calculator. Estimate total gas fees, breakeven resale floor price, and cost per item for Ethereum, Polygon, and other EVM mints."
        path="/calculators/nft-mint-cost"
        schema={[
          softwareApplicationSchema('NFT Mint Cost Calculator', 'Estimate NFT minting gas costs and breakeven resale price.', 'https://www.mintcalculator.com/calculators/nft-mint-cost'),
          faqPageSchema(faqItems),
          breadcrumbSchema([{ name: 'Home', path: '/' }, { name: 'NFT Mint Cost Calculator', path: '/calculators/nft-mint-cost' }]),
        ]}
      />
      <div className="mx-auto max-w-7xl px-4 py-10 sm:px-6 lg:px-8">
        <Breadcrumbs items={[{ name: 'Home', path: '/' }, { name: 'NFT Mint Cost Calculator', path: '/calculators/nft-mint-cost' }]} />
        <SectionHeading
          eyebrow="Web3 & Crypto Mint Gas"
          title="NFT & Web3 Mint Cost Calculator"
          description="Estimate total mint cost including gas fees, and calculate the resale floor price needed to break even."
        />

        <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-5">
          <div className="space-y-5 rounded-xl border border-slate-800 bg-slate-900/50 p-5 lg:col-span-2">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-300">Network</label>
              <div className="grid grid-cols-2 gap-2">
                {(['ethereum', 'polygon'] as Network[]).map(net => (
                  <button
                    key={net}
                    onClick={() => handleNetworkChange(net)}
                    className={`rounded-lg border px-3 py-2 text-sm font-medium capitalize transition-colors ${network === net ? 'border-emerald-500 bg-emerald-500/10 text-emerald-400' : 'border-slate-700 text-slate-400 hover:border-slate-600'}`}
                  >
                    {net}
                  </button>
                ))}
              </div>
            </div>
            <NumberInput label={`Mint price (${symbol})`} value={mintPrice} onChange={setMintPrice} step={0.01} />
            <NumberInput label="Mint quantity" value={quantity} onChange={setQuantity} step={1} />
            <NumberInput label="Gas price (Gwei)" value={gasPrice} onChange={setGasPrice} step={1} accent="cyan" />
            <NumberInput label="Gas limit (ERC-721/1155)" value={gasLimit} onChange={setGasLimit} step={5000} accent="cyan" />
            <NumberInput label={`${symbol} USD spot price`} value={tokenPrice} onChange={setTokenPrice} prefix="$" step={10} accent="teal" />
            <NumberInput label="Marketplace / royalty fee %" value={royaltyPct} onChange={setRoyaltyPct} suffix="%" step={0.5} accent="amber" />
          </div>

          <div className="grid grid-cols-2 gap-3 lg:col-span-3">
            <StatCard label="Total Mint Cost" value={`${results.totalNative.toFixed(4)} ${symbol}`} accent="emerald" size="lg" sublabel={formatCurrency(results.totalUsd)} />
            <StatCard label="Gas Fee Total" value={`${results.totalGasNative.toFixed(5)} ${symbol}`} accent="cyan" sublabel={formatCurrency(results.gasUsd)} />
            <StatCard label="Cost Per Item" value={formatCurrency(results.costPerItem)} accent="teal" />
            <StatCard label="Breakeven Floor Price" value={formatCurrency(results.breakevenFloor)} accent="amber" sublabel="After marketplace/royalty fees" />
            <StatCard label="Wallet Safety Buffer" value={formatCurrency(results.safetyBuffer)} accent="slate" sublabel="Recommended balance (+15%)" />
          </div>
        </div>

        <div className="mt-6">
          <DisclaimerNote text="Gas prices and token prices are volatile and change block-to-block. Always verify live network conditions before minting." />
        </div>

        <div className="mt-16 max-w-3xl">
          <SectionHeading title="Frequently Asked Questions" />
          <div className="mt-6">
            <FaqAccordion items={faqItems} />
          </div>
        </div>
      </div>
    </>
  )
}

export default NftMintCost
